import React, { useEffect, useState, useRef } from "react";
import { X, Paperclip, Bold, Italic, Underline, Strikethrough, Heading1, Heading2, ListOrdered, List, Type, Image as ImageIcon, Link as LinkIcon, Code, Send, Trash2, Download, Upload, FolderOpen, Loader2, CheckSquare, Square, Plus } from "lucide-react";
import { api, filesApi } from "../../api";
import { avatarColorFor, initialsFor, COLUMNS, CATEGORY_TAGS } from "./constants";

/**
 * Task Detail modal:
 *  - Title / status / dates / progress
 *  - Attachments list + upload
 *  - Threaded comments with rich text-style toolbar + @mentions rendering
 */
export default function TaskDetail({ task, onClose, onChange, currentUser }) {
  const [local, setLocal] = useState(task);
  const [text, setText] = useState("");
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const commentImgRef = useRef(null);
  const [imgMenuOpen, setImgMenuOpen] = useState(false);
  const [hexaPickerOpen, setHexaPickerOpen] = useState(false);

  useEffect(() => setLocal(task), [task]);

  if (!local) return null;

  const save = async (patch) => {
    setLocal(l => ({ ...l, ...patch }));
    try {
      const { data } = await api.put(`/tasks/${local.id}`, patch);
      onChange?.(data);
    } catch (e) { /* noop */ }
  };

  const addComment = async () => {
    const html = editorRef.current?.innerHTML?.trim();
    const raw  = editorRef.current?.innerText?.trim();
    if (!raw) return;
    const authorName = currentUser?.name || (currentUser?.address || "").split("@")[0] || "You";
    const { data } = await api.post(`/tasks/${local.id}/comments`, {
      text: html,
      author_name: authorName,
      avatar_color: avatarColorFor(authorName),
    });
    const next = { ...local, comments: [...(local.comments || []), data] };
    setLocal(next); onChange?.(next);
    if (editorRef.current) editorRef.current.innerHTML = "";
    setText("");
  };

  const deleteComment = async (cid) => {
    await api.delete(`/tasks/${local.id}/comments/${cid}`);
    const next = { ...local, comments: (local.comments || []).filter(c => c.id !== cid) };
    setLocal(next); onChange?.(next);
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Max attachment size is 5MB"); return; }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const { data } = await api.post(`/tasks/${local.id}/attachments`, {
      name: file.name, mime: file.type, size: file.size, data: b64,
    });
    // Server returns metadata only — merge back the data URL we already have
    const newAtt = { ...data, data: b64 };
    const next = { ...local, attachments: [...(local.attachments || []), newAtt] };
    setLocal(next); onChange?.(next);
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteAttachment = async (aid) => {
    await api.delete(`/tasks/${local.id}/attachments/${aid}`);
    const next = { ...local, attachments: (local.attachments || []).filter(a => a.id !== aid) };
    setLocal(next); onChange?.(next);
  };

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };

  const insertCommentImage = (dataUrl) => {
    if (!dataUrl) return;
    editorRef.current?.focus();
    // Slight delay to ensure focus lands inside the editor before insertion.
    setTimeout(() => {
      document.execCommand("insertImage", false, dataUrl);
      if (editorRef.current) setText(editorRef.current.innerText);
    }, 0);
  };

  const onPickDesktopCommentImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please choose an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Max image size is 5MB"); return; }
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result); r.onerror = rej;
      r.readAsDataURL(file);
    });
    insertCommentImage(dataUrl);
    if (commentImgRef.current) commentImgRef.current.value = "";
  };

  const col = COLUMNS.find(c => c.id === (local.status === "doing" ? "in_progress" : local.status === "done" ? "completed" : local.status)) || COLUMNS[0];

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 p-6" onClick={onClose}>
      <div
        className="bg-white text-slate-800 w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-lg shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="tp-task-detail"
      >
        <div className="px-5 py-3 flex items-center justify-between text-white" style={{ background: col.color }}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-white/25">{local.task_code || `Task-${(local.id || "").slice(0,3)}`}</span>
            <input
              value={local.title || ""}
              onChange={(e) => setLocal(l => ({ ...l, title: e.target.value }))}
              onBlur={(e) => save({ title: e.target.value })}
              className="bg-transparent placeholder-white/70 text-white text-lg font-bold outline-none flex-1 min-w-0 truncate"
              placeholder="Task title"
              data-testid="tp-task-title"
            />
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="tp-detail-close"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Meta grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b border-slate-100 bg-slate-50">
            <MetaSelect label="Status" value={local.status || "todo"} onChange={(v) => save({ status: v })}
              options={COLUMNS.map(c => [c.id, c.label])} />
            <MetaSelect label="Priority" value={local.priority || "normal"} onChange={(v) => save({ priority: v })}
              options={[["low","Low"],["normal","Normal"],["medium","Medium"],["high","High"],["urgent","Urgent"]]} />
            <MetaField label="Start" type="date" value={local.start_date || ""} onChange={(v) => save({ start_date: v })} />
            <MetaField label="Due" type="date" value={local.due || ""} onChange={(v) => save({ due: v })} />
          </div>

          {/* Category tag chips */}
          <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1">Tag</span>
            {CATEGORY_TAGS.map(t => (
              <button
                key={t.label}
                onClick={() => save({ category_tag: t.label, category_color: t.color })}
                className={`text-[10px] font-bold px-2 py-1 rounded ${local.category_tag === t.label ? "ring-2 ring-offset-1" : ""}`}
                style={{ background: t.color, color: "white", ringColor: t.color }}
                data-testid={`tp-tag-${t.label}`}
              >{t.label}</button>
            ))}
          </div>

          {/* Description */}
          <div className="p-4 border-b border-slate-100">
            <label className="text-[11px] uppercase tracking-widest text-slate-500">Description</label>
            <textarea
              value={local.description || ""}
              onChange={(e) => setLocal(l => ({ ...l, description: e.target.value }))}
              onBlur={(e) => save({ description: e.target.value })}
              rows={3}
              className="mt-1 w-full border border-slate-200 rounded p-2 text-sm outline-none focus:border-indigo-400 bg-white text-slate-800"
              placeholder="What needs to be done…"
              data-testid="tp-task-desc"
            />
          </div>

          {/* Subtasks (checklist) */}
          <SubtasksSection local={local} setLocal={setLocal} save={save} />

          {/* Attachments */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><Paperclip size={11} /> Attachments</label>
              <button onClick={() => fileRef.current?.click()} className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700" data-testid="tp-attach-btn">
                + Attach file
              </button>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadAttachment(e.target.files?.[0])} data-testid="tp-attach-input" />
            </div>
            {(local.attachments || []).length === 0 && <div className="text-xs text-slate-400">No attachments yet.</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(local.attachments || []).map(a => (
                <div key={a.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded bg-slate-50">
                  <div className="w-9 h-9 bg-indigo-100 text-indigo-600 rounded flex items-center justify-center"><Paperclip size={14} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{a.name}</div>
                    <div className="text-[10px] text-slate-500">{Math.round((a.size||0)/1024)} KB</div>
                  </div>
                  {a.data && <a href={a.data} download={a.name} className="p-1 hover:bg-white rounded" title="Download"><Download size={12} /></a>}
                  <button onClick={() => deleteAttachment(a.id)} className="p-1 hover:bg-white rounded text-red-500" title="Delete" data-testid={`tp-attach-del-${a.id}`}><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div className="p-4">
            <label className="text-[11px] uppercase tracking-widest text-slate-500">Comments</label>
            <div className="mt-3 relative pl-8">
              <div className="absolute left-3 top-2 bottom-0 w-px bg-red-300" />
              {(local.comments || []).map((c) => (
                <div key={c.id} className="relative mb-4">
                  <div className="absolute -left-8 top-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow"
                       style={{ background: c.avatar_color || avatarColorFor(c.author_name || "?") }}>
                    {initialsFor(c.author_name || c.author || "?")}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-slate-800">{c.author_name || c.author}</span>
                    <span className="text-[10px] text-slate-400">Created {timeAgo(c.created)}</span>
                    {c.author === (currentUser?.address) && (
                      <button onClick={() => deleteComment(c.id)} className="ml-auto text-[10px] text-red-500 hover:underline" data-testid={`tp-com-del-${c.id}`}>Delete</button>
                    )}
                  </div>
                  <div
                    className="text-sm text-slate-700 mt-1"
                    dangerouslySetInnerHTML={{ __html: linkifyMentions(c.text || "") }}
                  />
                </div>
              ))}
              {(local.comments || []).length === 0 && <div className="text-xs text-slate-400 pl-1 mb-3">No comments yet.</div>}
            </div>

            {/* Rich text toolbar + editor */}
            <div className="mt-2 border border-slate-200 rounded">
              <div className="flex items-center gap-1 border-b border-slate-200 px-2 py-1 flex-wrap bg-slate-50">
                <TBtn onClick={() => exec("bold")}          title="Bold"><Bold size={13} /></TBtn>
                <TBtn onClick={() => exec("italic")}        title="Italic"><Italic size={13} /></TBtn>
                <TBtn onClick={() => exec("underline")}     title="Underline"><Underline size={13} /></TBtn>
                <TBtn onClick={() => exec("strikeThrough")} title="Strike"><Strikethrough size={13} /></TBtn>
                <div className="w-px h-4 bg-slate-300 mx-1" />
                <TBtn onClick={() => exec("formatBlock", "H1")} title="H1"><Heading1 size={13} /></TBtn>
                <TBtn onClick={() => exec("formatBlock", "H2")} title="H2"><Heading2 size={13} /></TBtn>
                <TBtn onClick={() => exec("insertOrderedList")}  title="Numbered"><ListOrdered size={13} /></TBtn>
                <TBtn onClick={() => exec("insertUnorderedList")} title="Bulleted"><List size={13} /></TBtn>
                <TBtn onClick={() => exec("removeFormat")}   title="Clear"><Type size={13} /></TBtn>
                <div className="w-px h-4 bg-slate-300 mx-1" />
                <div className="relative">
                  <TBtn onClick={() => setImgMenuOpen(v => !v)} title="Insert image"><ImageIcon size={13} /></TBtn>
                  {imgMenuOpen && (
                    <div
                      className="absolute z-20 mt-1 left-0 w-48 bg-white border border-slate-200 rounded shadow-lg py-1 text-xs text-slate-700"
                      onMouseLeave={() => setImgMenuOpen(false)}
                      data-testid="tp-comment-image-menu"
                    >
                      <button
                        onClick={() => { setImgMenuOpen(false); commentImgRef.current?.click(); }}
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-100 flex items-center gap-2"
                        data-testid="tp-comment-image-upload"
                      >
                        <Upload size={12} /> Upload from computer
                      </button>
                      <button
                        onClick={() => { setImgMenuOpen(false); setHexaPickerOpen(true); }}
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-100 flex items-center gap-2"
                        data-testid="tp-comment-image-hexa"
                      >
                        <FolderOpen size={12} /> Pick from HEXA Files
                      </button>
                    </div>
                  )}
                  <input
                    ref={commentImgRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPickDesktopCommentImage(e.target.files?.[0])}
                    data-testid="tp-comment-image-file-input"
                  />
                </div>
                <TBtn onClick={() => { const u = prompt("Link URL"); if (u) exec("createLink", u); }} title="Link"><LinkIcon size={13} /></TBtn>
                <TBtn onClick={() => exec("formatBlock", "PRE")} title="Code"><Code size={13} /></TBtn>
              </div>
              <div
                ref={editorRef}
                contentEditable
                onInput={(e) => setText(e.currentTarget.innerText)}
                className="min-h-[76px] px-3 py-2 text-sm outline-none prose prose-sm max-w-none bg-white text-slate-800"
                data-placeholder="Enter Text"
                data-testid="tp-comment-editor"
                suppressContentEditableWarning
              />
              <div className="flex items-center justify-end p-2 border-t border-slate-200 bg-slate-50">
                <button
                  onClick={addComment}
                  disabled={!text.trim()}
                  className={`text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1 ${text.trim() ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}
                  data-testid="tp-comment-send"
                >
                  <Send size={12} /> Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>
        {hexaPickerOpen && (
          <HexaImagePicker
            onPick={(dataUrl) => { setHexaPickerOpen(false); insertCommentImage(dataUrl); }}
            onClose={() => setHexaPickerOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

function HexaImagePicker({ onPick, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await filesApi.all();
        // Only image files
        setItems((list || []).filter(f => f.type === "file" && (f.mime || "").startsWith("image/")));
      } catch { setItems([]); }
      setLoading(false);
    })();
  }, []);

  const pick = async (file) => {
    setDownloadingId(file.id);
    try {
      const { data: blob } = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result); r.onerror = reject;
        r.readAsDataURL(blob);
      });
      onPick(dataUrl);
    } catch (e) {
      alert("Could not load image from HEXA Files: " + (e.message || "error"));
      setDownloadingId(null);
    }
  };

  return (
    <div
      className="absolute inset-0 z-40 bg-black/50 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="tp-hexa-picker"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[80vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <FolderOpen size={14} className="text-indigo-500" /> Pick image from HEXA Files
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500"><X size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-slate-500 text-xs flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-slate-500 text-xs py-8">No image files found in your HEXA Files.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {items.map(f => (
                <button
                  key={f.id}
                  disabled={!!downloadingId}
                  onClick={() => pick(f)}
                  data-testid={`tp-hexa-pick-${f.id}`}
                  className="group text-left rounded border border-slate-200 hover:border-indigo-400 hover:shadow transition overflow-hidden disabled:opacity-60"
                >
                  <div className="aspect-video bg-slate-100 grid place-items-center relative">
                    {downloadingId === f.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                    ) : (
                      <ImageIcon size={20} className="text-slate-400" />
                    )}
                  </div>
                  <div className="px-2 py-1.5">
                    <div className="text-[11px] font-medium text-slate-800 truncate" title={f.name}>{f.name}</div>
                    <div className="text-[10px] text-slate-400">{f.mime}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TBtn({ children, onClick, title }) {
  return (
    <button onClick={onClick} title={title}
      className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-200 text-slate-700">
      {children}
    </button>
  );
}

function MetaField({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</label>
      <input type={type} value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        style={{ colorScheme: "light" }}
        className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 text-xs bg-white text-slate-800" />
    </div>
  );
}

function MetaSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</label>
      <select value={value} onChange={(e) => onChange?.(e.target.value)}
        className="mt-1 w-full border border-slate-200 rounded px-2 py-1.5 text-xs bg-white">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}

function linkifyMentions(html) {
  return (html || "").replace(/@([A-Za-z0-9._-]+)/g, '<a href="#" class="text-indigo-600 font-medium">@$1</a>');
}


/* Subtasks / checklist section — stored on the task's `checklist` array via PUT /tasks/:id */
function SubtasksSection({ local, setLocal, save }) {
  const items = Array.isArray(local.checklist) ? local.checklist : [];
  const [text, setText] = useState("");

  const persist = (nextItems) => {
    setLocal(l => ({ ...l, checklist: nextItems }));
    save({ checklist: nextItems });
  };

  const add = () => {
    const t = text.trim();
    if (!t) return;
    const next = [
      ...items,
      { id: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, title: t, done: false },
    ];
    setText("");
    persist(next);
  };

  const toggle = (id) => {
    persist(items.map(it => it.id === id ? { ...it, done: !it.done } : it));
  };

  const remove = (id) => {
    persist(items.filter(it => it.id !== id));
  };

  const doneCount = items.filter(i => i.done).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  return (
    <div className="p-4 border-b border-slate-100" data-testid="tp-subtasks">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
          <CheckSquare size={11} /> Subtasks
        </label>
        {items.length > 0 && (
          <span className="text-[10px] text-slate-500 mono tracking-widest">{doneCount}/{items.length} · {pct}%</span>
        )}
      </div>

      {items.length > 0 && (
        <div className="mb-3 h-1 w-full bg-slate-100 rounded overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="space-y-1 mb-2">
        {items.map(it => (
          <div key={it.id} className="flex items-center gap-2 group" data-testid={`tp-subtask-${it.id}`}>
            <button
              onClick={() => toggle(it.id)}
              data-testid={`tp-subtask-toggle-${it.id}`}
              className={`shrink-0 w-4 h-4 rounded border transition ${it.done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300 hover:border-emerald-400"} flex items-center justify-center`}
            >
              {it.done ? <CheckSquare size={11} /> : <Square size={11} className="opacity-0" />}
            </button>
            <div className={`flex-1 text-sm ${it.done ? "line-through text-slate-400" : "text-slate-700"}`}>
              {it.title}
            </div>
            <button
              onClick={() => remove(it.id)}
              data-testid={`tp-subtask-del-${it.id}`}
              className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 rounded"
              title="Delete subtask"
            >
              <Trash2 size={11} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-slate-400">No subtasks yet — add checklist items below.</div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add a subtask and press Enter"
          className="flex-1 text-sm px-2 py-1.5 border border-slate-200 rounded outline-none focus:border-indigo-400"
          data-testid="tp-subtask-input"
        />
        <button
          onClick={add}
          disabled={!text.trim()}
          data-testid="tp-subtask-add"
          className="text-xs font-semibold px-2.5 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
        >
          <Plus size={11} /> Add
        </button>
      </div>
    </div>
  );
}
